import {
  admissibleDomainEnds,
  normalizeCelsiusUnit,
  parseNumericInterval,
  LimitMetricSpec,
} from '../../../../../../common/thermostat-limits';
import { convertLimitTemperature } from '../../../util/convert-temp-units';
import { CurrentAttributes, MyThermostat } from '../types';
import {
  MitsubishiTargetProfile,
  MITSUBISHI_TARGET_MIN_CELSIUS,
  MITSUBISHI_TARGET_MAX_CELSIUS,
  mitsubishiTargetCelsius,
  mitsubishiTargetFahrenheit,
} from './mitsubishi-target-display';

type TargetKey = 'heatSetpoint' | 'coolSetpoint' | 'setpoint';

// These selected GraphQL virtuals expose the canonical auto capability. Missing,
// contradictory or dual-target capability is not a qualified native target.
function nativeTargetMetadata(
  device: MyThermostat | undefined,
  key: TargetKey,
  requireWritable = true,
) {
  if (device?.source?.type !== 'Conductor' || !device.isMitsubishiThermostat)
    return;
  const single =
    device.supportsAutoMode === true &&
    device.supportsSingleSetpointAutoMode === true &&
    device.supportsDualSetpointAutoMode === false;
  const noAuto =
    device.supportsAutoMode === false &&
    device.supportsSingleSetpointAutoMode === false &&
    device.supportsDualSetpointAutoMode === false;
  if ((!single && !noAuto) || (key === 'setpoint' && !single)) return;
  const target = device.deviceMetrics?.find((entry) => entry?.key === key)
    ?.value as LimitMetricSpec | undefined;
  const parsed = parseNumericInterval(target?.value);
  if (
    !target ||
    !parsed.ok ||
    !normalizeCelsiusUnit(target.units ?? target.unit) ||
    (target.units != null &&
      target.unit != null &&
      normalizeCelsiusUnit(target.units) !==
        normalizeCelsiusUnit(target.unit)) ||
    !['number', 'decimal', 'float', 'double', 'integer', 'int'].includes(
      String(target.valueType).toLowerCase(),
    ) ||
    (requireWritable
      ? target.readOnly !== false
      : typeof target.readOnly !== 'boolean') ||
    typeof target.valueStep !== 'number' ||
    !Number.isFinite(target.valueStep) ||
    target.valueStep <= 0
  )
    return;
  return { interval: parsed.value, step: target.valueStep };
}

/** Today's qualified Conductor producer requires TempDetail/TempDetailC/
 * TempDetail_C to agree before publishing this native step. That producer
 * contract makes 0.5/1 Celsius identify the detailed/whole Fahrenheit profile.
 * Do not generalize this inference if differing native flags become supported.
 * Read-only descriptors may format reported values; they never grant editing.
 */
function nativeTargetProfile(
  device: MyThermostat | undefined,
  key: TargetKey,
): MitsubishiTargetProfile | undefined {
  const target = nativeTargetMetadata(device, key, false);
  return target?.step === 0.5
    ? 'detail'
    : target?.step === 1
      ? 'whole'
      : undefined;
}

/** Render-only labels. Drafts remain exact preferred-unit representations of
 * canonical Celsius; rounded/many-to-one labels must never enter the serializer.
 */
export function nativeTargetDisplayValue(
  device: MyThermostat | undefined,
  key: TargetKey,
  value: number | null | undefined,
  units: 'C' | 'F',
): number | undefined {
  if (value == null) return;
  if (
    units !== 'F' ||
    device?.source?.type !== 'Conductor' ||
    !device.isMitsubishiThermostat
  )
    return value;
  if (!Number.isFinite(value)) return;
  const profile = nativeTargetProfile(device, key);
  const celsius = convertLimitTemperature(value, 'C', units);
  const mapped =
    profile && celsius !== undefined
      ? mitsubishiTargetFahrenheit(celsius, profile)
      : undefined;
  // Missing/unqualified profiles retain integer arithmetic presentation. This
  // fallback makes no claim of matching a wall controller and adds no authority.
  return mapped ?? Math.round(value);
}

/** Native target metadata is part of edit authority, not ordinary telemetry.
 * A changed grid/range must invalidate drafts before display or Save uses it. */
export function nativeTargetCapabilitySignature(device?: MyThermostat) {
  if (device?.source?.type !== 'Conductor' || !device.isMitsubishiThermostat)
    return;
  return JSON.stringify(
    device.deviceMetrics
      ?.filter((entry) =>
        ['heatSetpoint', 'coolSetpoint', 'setpoint'].includes(entry?.key ?? ''),
      )
      .map((entry) => [
        entry?.key,
        {
          value: entry?.value?.value,
          valueStep: entry?.value?.valueStep,
          units: entry?.value?.units,
          unit: entry?.value?.unit,
          valueType: entry?.value?.valueType,
          readOnly: entry?.value?.readOnly,
        },
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  );
}

/** Preserve the source grid's visible precision only for qualified native targets. */
export function nativeTargetDigits(
  device: MyThermostat | undefined,
  key: TargetKey,
  units: 'C' | 'F',
): number | undefined {
  if (
    units === 'F' &&
    device?.source?.type === 'Conductor' &&
    device.isMitsubishiThermostat
  )
    return 0;
  const target = nativeTargetMetadata(device, key);
  if (!target) return;
  const step = target.step;
  for (let digits = 0; digits < 12; digits++) {
    const scaled = step * 10 ** digits;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-8) return digits;
  }
  return 12;
}

/** Choose the next representable source target inside both native and policy
 * bounds. Display-unit increments cannot represent a Celsius source grid. */
export function nextNativeTarget(
  device: MyThermostat | undefined,
  values: Partial<CurrentAttributes>,
  key: TargetKey,
  delta: number,
  units: 'C' | 'F',
): number | undefined {
  const target = nativeTargetMetadata(device, key);
  const current = convertLimitTemperature(values[key], 'C', units);
  if (
    !target ||
    current === undefined ||
    !Number.isFinite(delta) ||
    delta === 0
  )
    return;
  const heat = key !== 'coolSetpoint';
  const cool = key !== 'heatSetpoint';
  const lows = [
    ...(heat ? [values.minHeatSetpointLimit] : []),
    ...(cool ? [values.minCoolSetpointLimit] : []),
  ].map((value) => convertLimitTemperature(value, 'C', units));
  const highs = [
    ...(heat ? [values.maxHeatSetpointLimit] : []),
    ...(cool ? [values.maxCoolSetpointLimit] : []),
  ].map((value) => convertLimitTemperature(value, 'C', units));
  if ([...lows, ...highs].some((value) => value === undefined)) return;
  const lower = Math.max(target.interval.lower, ...(lows as number[]));
  const upper = Math.min(target.interval.upper, ...(highs as number[]));
  if (lower > upper) return;
  const interval = {
    lower: delta > 0 ? Math.max(lower, current) : lower,
    upper: delta < 0 ? Math.min(upper, current) : upper,
    lowerInclusive:
      (lower > target.interval.lower || target.interval.lowerInclusive) &&
      !(delta > 0 && current >= lower),
    upperInclusive:
      (upper < target.interval.upper || target.interval.upperInclusive) &&
      !(delta < 0 && current <= upper),
  };
  if (interval.lower > interval.upper) return;
  const ends = admissibleDomainEnds({ interval, step: target.step });
  let candidate = delta > 0 ? ends.minimum : ends.maximum;
  const profile = units === 'F' ? nativeTargetProfile(device, key) : undefined;
  const currentLabel = profile
    ? mitsubishiTargetFahrenheit(current, profile)
    : undefined;
  if (profile && currentLabel !== undefined) {
    // Walk the qualified native grid, skipping values with the same visible
    // Fahrenheit label. Bound the search by the captured reference extent.
    const referenceSteps =
      (MITSUBISHI_TARGET_MAX_CELSIUS - MITSUBISHI_TARGET_MIN_CELSIUS) /
        target.step +
      1;
    for (
      let visited = 0;
      candidate !== undefined && visited < referenceSteps;
      visited++
    ) {
      const label = mitsubishiTargetFahrenheit(candidate, profile);
      if (label === undefined) break;
      if (
        (delta > 0 && label > currentLabel) ||
        (delta < 0 && label < currentLabel)
      ) {
        const preferred = mitsubishiTargetCelsius(label, profile);
        // Vendor inverses can prefer a different native value with the same
        // label. Use that preference only inside the active Celsius policy and
        // direction; a narrow bound may require the other valid native value.
        if (
          preferred !== undefined &&
          (preferred > interval.lower ||
            (preferred === interval.lower && interval.lowerInclusive)) &&
          (preferred < interval.upper ||
            (preferred === interval.upper && interval.upperInclusive)) &&
          Math.abs(
            preferred / target.step - Math.round(preferred / target.step),
          ) < 1e-8 &&
          mitsubishiTargetFahrenheit(preferred, profile) === label
        )
          candidate = preferred;
        return convertLimitTemperature(candidate, units);
      }
      const remaining = {
        ...interval,
        ...(delta > 0
          ? { lower: candidate, lowerInclusive: false }
          : { upper: candidate, upperInclusive: false }),
      };
      const next = admissibleDomainEnds({
        interval: remaining,
        step: target.step,
      });
      candidate = delta > 0 ? next.minimum : next.maximum;
    }
    if (
      candidate !== undefined &&
      mitsubishiTargetFahrenheit(candidate, profile) !== undefined
    )
      return;
  }
  return convertLimitTemperature(candidate, units);
}
